import { ChangeDetectorRef, Component, computed, effect, ElementRef, HostListener, inject, NgZone, signal, untracked, ViewChild } from '@angular/core';
import { readBreakpoint } from 'src/app/utils/breakpoint.util';
import { PreviewerType } from 'src/app/types/previewer.type';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { PreviewerComponent } from '../previewer/previewer.component';
import { Navigation2Service } from '../../services/navigation2.service';
import { CheckoutService } from '../../services/checkout.service';
import { MatDialog } from '@angular/material/dialog';
import { CustomerDetailsErrorDialogComponent } from '../customer-details-error.dialog/customer-details-error.dialog.component';
import { DesignsService } from 'src/app/services/designs/designs.service';
import { EngravingTypesService } from 'src/app/services/engraving-types/engraving-types.service';
import { FormsService } from 'src/app/services/forms/forms.service';
import { ProfileGroupsService } from 'src/app/services/profile-groups/profile-groups.service';
import { ProfileWeightsService } from 'src/app/services/profile-weights/profile-weights.service';
import { MaterialsService } from 'src/app/services/materials/materials.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { WidthsService } from 'src/app/services/widths/widths.service';
import { SkuMapService } from 'src/app/services/sku-map/sku-map.service';
import { NonClosableErrorDialogComponent } from '../non-closable-error.dialog/non-closable-error.dialog.component';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { assert, assertTrue } from 'src/app/utils/assert.util';
import { environment } from 'src/environments/environment';
import OpenReplay from '@openreplay/tracker';
import { AdminControlsComponent } from '../admin-controls/admin-controls.component';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { OpenReplayService } from 'src/app/services/open-replay/open-replay.service';
import { IframeService } from 'src/app/services/iframe/iframe.service';
import { StoneArrangementsService } from 'src/app/services/stone-arrangements/stone-arrangements.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneSettingsService } from 'src/app/services/stone-settings/stone-settings.service';
import { StoneShapesService } from 'src/app/services/stone-shapes/stone-shapes.service';
import { StoneSizesService } from 'src/app/services/stone-sizes/stone-sizes.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneTypesService } from 'src/app/services/stone-types/stone-types.service';
import { TopBarService } from 'src/app/services/top-bar-service/top-bar-service.service';
import { InvalidSkuDialogComponent } from '../invalid-sku.dialog/invalid-sku.dialog.component';
import { DispatcherService } from 'src/app/services/dispatcher/dispatcher.service';
import { MainImageService } from 'src/app/services/main-image/main-image.service';
import { AttributeMenuComponent } from '../attribute-menu/attribute-menu.component';
import { ViewTitleButton } from 'src/app/interfaces/view-title-button';
import { NavigationAction } from 'src/app/interfaces/navigation-action';
import { ShareService } from 'src/app/services/share/share.service';
import { ScrollService } from 'src/app/services/scroll/scroll.service';
import { CustomerThemeService } from 'src/app/services/customer-theme/customer-theme.service';


@Component({
  selector: 'app-main-root',
  templateUrl: './main-root.component.html',
  styleUrls: ['./main-root.component.scss']
})
export class MainRootComponent {
  private mainImageService = inject(MainImageService);
  private dispatcherService = inject(DispatcherService);
  private openReplayService = inject(OpenReplayService);
  private zone = inject(NgZone);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private navigationService = inject(Navigation2Service);
  private checkoutService = inject(CheckoutService);
  private dialog = inject(MatDialog);
  private attributeSelectionService = inject(SelectedAttributeService);
  private skuMapService = inject(SkuMapService);
  private engravingTypesService = inject(EngravingTypesService);
  private configuredItemService = inject(ConfiguredItemService);
  private loadingService = inject(LoadingService);
  private apiServices = [inject(DesignsService), inject(FormsService), inject(ProfileGroupsService), inject(ProfileWeightsService), inject(MaterialsService), inject(SizesService), inject(WidthsService), this.engravingTypesService, inject(StoneTypesService), inject(StoneTypesWithShapesService), inject(StoneShapesService), inject(StoneArrangementsService), inject(StoneSettingsService), inject(StoneArrangementsWithSettingsService), inject(StoneSizesService)];
  private iframeService = inject(IframeService);
  private topBarService = inject(TopBarService);
  private shareService = inject(ShareService);
  private scrollService = inject(ScrollService);
  private customerThemeService = inject(CustomerThemeService);
  selectedCustomizationCategoryIndex = 0;
  selectedPreviewerType: PreviewerType = 'Preview';
  selectedAttributes: Map<string, string | null> | null = null
  shouldDarkenImage = false;
  isRetailerOnly = signal(false);
  isCustomizerHidden = signal<boolean>(false);
  customizerWidth = signal<number>(0);
  customizerWidthString = computed(() => `${this.customizerWidth()}px`);
  /**
   * The 600px cutoff comes from $small-screen-width in variables.scss, via the --breakpoint-* custom
   * property styles.scss puts on :root. The toolbar's .MenuButton query uses the same value through
   * the media-small-screen mixin; both were bare 600s in two languages before (ADO #8909).
   */
  smallScreenWidth = readBreakpoint('--breakpoint-small-screen-width', 600);
  isSmallScreen = signal(false);
  @ViewChild('customizer', { static: true, read: ElementRef }) customizer?: ElementRef<HTMLElement>;
  @ViewChild(PreviewerComponent) previewer?: PreviewerComponent;
  isLoaded = signal<boolean>(false);
  loadingServiceIsLoading: boolean = false;
  initialEngravingText = signal<string | null>(null);
  initialEngravingType = signal<string | null>(null);
  isRetailerCartEnabled = computed(() => this.checkoutService.isRetailerCartEnabled());
  themeLoaded = signal(false);
  activeTopBarItem = computed(() => this.topBarService.activeItem());

  primaryPalette = computed(() => {
    const primary = this.customerThemeService.theme()?.primaryColor;
    return primary ? this.computePalette(primary) : null;
  });

  accentPalette = computed(() => {
    const accent = this.customerThemeService.theme()?.secondaryColor;
    return accent ? this.computePalette(accent) : null;
  });

  themeStyles = computed(() => {
    const styles: Record<string, string> = {};
    const primary = this.primaryPalette();
    const accent = this.accentPalette();
    if (primary) {
      styles['--primary-color'] = primary[500];
      styles['--primary-lightest'] = primary[50];
      styles['--primary-lighter'] = primary[100];
      styles['--primary-light'] = primary[300];
      styles['--primary-dark'] = primary[700];
    }
    if (accent) {
      styles['--accent'] = accent[500];
    }
    return styles;
  });

  constructor() {
    (window as any).navigationService = this.navigationService;
    (window as any).TextureControls = AdminControlsComponent;

    this.navigationService.globalTitleButtons.set([
      {
        key: 'menu',
        icon: 'menu',
        location: 'left',
        onClick: (button: ViewTitleButton) => {
          const shouldPresentMenu = !this.navigationService.siblingViews.some(v => v.title === 'Menu');
          this.navigationService.dismissAll();
          if (!shouldPresentMenu) {
            button.icon = 'menu';
          } else {
            button.icon = 'close';
            this.navigationService.present({ title: 'Menu', type: AttributeMenuComponent, doesAppearInProgressBar: false, shouldHideTitle: true });
          }
        }
      },
      {
        key: 'previous',
        icon: 'arrow_back_ios',
        location: 'left',
        iconStyle: () => ({
          scale: '0.7'
        }),
        style: () => {
          const style: Record<string, string> = {};
          if (!this.navigationService.canNavigateBack()) {
            style['color'] = '#8b8b8b';
            style['pointer-events'] = 'none';
          }
          return style;
        },
        onClick: () => this.navigationService.canNavigateBack() && this.navigationService.back()
      },
      {
        key: 'next',
        icon: 'arrow_forward_ios',
        location: 'right',
        iconStyle: () => ({
          scale: '0.7'
        }),
        style: () => {
          const style: Record<string, string> = {};
          if (!this.navigationService.canNavigateNext()) {
            style['color'] = '#8b8b8b';
            style['pointer-events'] = 'none';
          }
          return style;
        },
        onClick: () => this.navigationService.canNavigateNext() && this.navigationService.next()
      }
    ]);

    this.navigationService.subscribe((action: NavigationAction) => {
      if (['Dismiss', 'DismissAll'].includes(action.action) && (action.data instanceof Array && action.data.some(v => v.title === 'Menu') || action.data?.title === 'Menu')) {
        this.navigationService.globalTitleButtons.update(buttons => {
          const menuButton = buttons.find(b => b.key === 'menu')
          if (menuButton) {
            menuButton.icon = 'menu';
          }
          return buttons;
        });
      }
    });

    this.loadingService.isLoading.subscribe((isLoading) => {
      this.loadingServiceIsLoading = isLoading;
    });

    effect(() => {
      if (this.navigationService.activeViewIndex() && this.isCustomizerHidden()) {
        this.isCustomizerHidden.set(false);
        this.changeDetectorRef.detectChanges();
      }
    }, { allowSignalWrites: true })

    effect(() => {
      const latestMessage = this.iframeService.latestMessage();

      if (latestMessage) {
        try {
          const messageObject = JSON.parse(latestMessage);

          if (messageObject.type === 'Benchmark::RingLab::setengraving') {
            if (!untracked(() => this.selectedAttributeService.configuredItem()).form) {
              this.initialEngravingType.set(messageObject.value.engravingType);
              this.initialEngravingText.set(messageObject.value.engravingText);
            } else {
              untracked(() => this.selectedAttributeService.mutate((configuredItem) => {
                configuredItem.engravingType = untracked(() => this.engravingTypesService.values()).find(type => type.shortName === messageObject.value.engravingType) ?? null;
                configuredItem.engravingText = messageObject.value.engravingText;
              }, 'engravingText', true, false));
            }
          } else if (this.checkoutService.isRetailerOnly() ? environment.openReplayIframeKey : environment.openReplayKey && environment.openReplayIngestPoint && messageObject.type === 'Benchmark::RingLab::retailerwebsite') {
            this.zone.runOutsideAngular(() => {
              this.openReplayService.sendMetaData('retailer', messageObject.value);
            });
          }
        } catch { }
      }
    }, { allowSignalWrites: true });
  }

  computePalette(primaryHex: string): Record<number, string> {
    const { h, s, l } = this.hexToHsl(primaryHex);
    const ratios: Record<number, number> = {
      50: 46 / 52, 100: 37 / 52, 200: 26 / 52, 300: 16 / 52, 400: 8 / 52,
      600: -5 / 52, 700: -13 / 52, 800: -20 / 52, 900: -28 / 52,
    };
    const lightRange = 100 - l;
    const palette: Record<number, string> = { 500: `hsl(${h}, ${s}%, ${l}%)` };
    for (const [shade, ratio] of Object.entries(ratios)) {
      const lightness = Math.min(100, Math.max(0, Math.round(l + ratio * lightRange)));
      palette[Number(shade)] = `hsl(${h}, ${s}%, ${lightness}%)`;
    }
    return palette;
  }

  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    const hexCodeOnly = hex.startsWith('#') ? hex.slice(1) : hex;
    const hexCodeToUse = hexCodeOnly.length === 3
      ? hexCodeOnly.split('').map(c => c + c).join('')
      : hexCodeOnly;

    const r = parseInt(hexCodeToUse.slice(0, 2), 16) / 255;
    const g = parseInt(hexCodeToUse.slice(2, 4), 16) / 255;
    const b = parseInt(hexCodeToUse.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) {
      return { h: 0, s: 0, l: Math.round(l * 100) };
    }

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    let h: number;
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6; break;
    }

    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  }

  async ngOnInit() {
    this.iframeService.sendMessage('Benchmark::RingLab::Loaded');
    const url = new URL(window.location.href);
    try {
      await this.checkoutService.setB2BInfoFromURL(url);
      if (this.checkoutService.analyticsAllowed()) {
        this.zone.runOutsideAngular(() => {
          const openReplayKey = this.checkoutService.isRetailerOnly() ? environment.openReplayIframeKey : environment.openReplayKey;
          const openReplayIngestPoint = environment.openReplayIngestPoint;
          if (openReplayKey && openReplayIngestPoint) {
            this.openReplayService.init(openReplayKey, openReplayIngestPoint);
          }
        });
      }
      if (this.checkoutService.analyticsAllowed()) {
        this.zone.runOutsideAngular(() => {
          const customerIdentifier = this.checkoutService.b2bData.customerToken ?? this.checkoutService.b2bData.customer;
          this.openReplayService.identifyUser(this.checkoutService.b2bData.email ?? customerIdentifier ?? this.checkoutService.b2bData.retailerIframeID);
          this.openReplayService.sendMetaData('p', `${this.checkoutService.b2bData.retailerIframeID ?? customerIdentifier ?? 'none'}`);
          this.openReplayService.sendMetaData('n', decodeURIComponent(this.checkoutService.b2bData.business) ?? 'none');
          this.openReplayService.sendMetaData('StyleSzQtrSz', `${this.checkoutService.b2bData.styleNumber ?? 'none'} | ${this.checkoutService.b2bData.fingerSize ?? 'none'} | ${this.checkoutService.b2bData.isQuarterSize ?? 'none'}`);
        });
      }
      this.isRetailerOnly.set(this.checkoutService.isRetailerOnly());
      this.selectedAttributeService.loadUndoRedoLists();
    } catch(e) {
      console.error(e);
      this.dialog.open(NonClosableErrorDialogComponent, { disableClose: true, maxWidth: 400, data: 'Malformed configuration data. Please close this tab and try again. If this problem persists, please contact customer service.' });
      throw e;
    }

    (async () => {
      try {
        if (!(navigator as any).gpu) {
          throw Error("WebGPU not supported.");
        }

        const adapter = await (navigator as any).gpu.requestAdapter();
        const device = await adapter?.requestDevice();
        const gpuInfo = {
          adapter,
          adapterFeatures: adapter ? Array.from(adapter.features) : [],
          device: device ?? null,
          deviceFeatures: device ? Array.from(device.features) : [],
          limits: device ? device.limits : null,
          preferredCanvasFormat: (navigator as any).gpu.getPreferredCanvasFormat(),
          wgslLanguageFeatures: Array.from((navigator as any).gpu.wgslLanguageFeatures.entries())
        };
        console.log('WebGPU Info:', gpuInfo);
      } catch {
        console.log('Cannot access WebGPU.');
      }
    })();

    if (this.checkoutService.analyticsAllowed()) {
      if (this.checkoutService.isRetailerOnly() && environment.iframeGATrackingID) {
        this.googleAnalyticsService.init(environment.iframeGATrackingID);
      } else if (environment.gaTrackingID) {
        this.googleAnalyticsService.init(environment.gaTrackingID);
      }
    }

    if (!this.isRetailerOnly()) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.dataset['cfasync'] = 'false';
      script.innerHTML = `if(!window.location.href.includes('/share')){window.purechatApi = { l: [], t: [], on: function () { this.l.push(arguments); } }; (function () { var done = false; var script = document.createElement('script'); script.async = true; script.type = 'text/javascript'; script.src = 'https://app.purechat.com/VisitorWidget/WidgetScript'; document.getElementsByTagName('HEAD').item(0).appendChild(script); script.onreadystatechange = script.onload = function (e) { if (!done && (!this.readyState || this.readyState == 'loaded' || this.readyState == 'complete')) { var w = new PCWidget({c: '18e63c24-cc4e-41fc-802b-4ef79f031c0a', f: true }); done = true; } }; })();}`;
      document.head.append(script);
    }

    await Promise.all([
      ...this.apiServices.map(s => s.cacheData(this.checkoutService.b2bData.retailerIframeID)),
      this.customerThemeService.cacheTheme(
        this.checkoutService.b2bData.retailerIframeID
          ? { iframeID: this.checkoutService.b2bData.retailerIframeID }
          : { ringBuilderID: this.checkoutService.b2bData.ringBuilderID ?? undefined }
      )
    ]);

    if (!this.checkoutService.b2bData.sessionKey) {
      const isCustomizerStyle = this.configuredItemService.isCustomStyle(this.checkoutService.b2bData.styleNumber);
      const haloStyle = isCustomizerStyle ? await this.checkoutService.getSimilarHaloSKUToSelected(this.checkoutService.b2bData.styleNumber) : this.checkoutService.b2bData.styleNumber;
      this.checkoutService.updateB2BData((b2bData) => {
        b2bData.po = !!b2bData.po ? decodeURIComponent(b2bData.po) : null;
        b2bData.eng =  !!b2bData.eng ? decodeURIComponent(b2bData.eng) : null;
        b2bData.sessionKey = crypto.randomUUID();
        b2bData.haloStyle = haloStyle;
        if (!isCustomizerStyle) {
          b2bData.selectedHaloStyleNumber = haloStyle;
        }
      });
    }

    const b2bEng = this.initialEngravingText() ?? (this.checkoutService.b2bData.eng ?? null);
    const b2bEngType = !b2bEng ? null : (this.engravingTypesService.values().find(et => this.initialEngravingType() ? et.shortName === this.initialEngravingType() : et.displayName.toLowerCase() === (this.checkoutService.b2bData.engFont?.toLowerCase())) ?? null);
    const b2bPO = this.checkoutService.b2bData.po;

    const ringBuilderSKU = await this.getCustomItemNumber(this.checkoutService.b2bData.styleNumber);
    const skuToUse = this.checkoutService.b2bData.customizerSKU ?? ringBuilderSKU;

    try {
      assertTrue(!!ringBuilderSKU && !!skuToUse, 'Could not resolve provided sku or style to customizer sku');

      if (this.configuredItemService.isCustomSKU(skuToUse)) {
        await this.attributeSelectionService.setConfiguredItemFromSKUWithEngravingAndPO(skuToUse, b2bEng, b2bEngType, b2bPO);
      } else {
        if (!this.checkoutService.b2bData.customizerSKU) {
          await this.attributeSelectionService.setConfiguredItemFromStyleWithEngravingAndPO(skuToUse, b2bEng, b2bEngType, b2bPO, this.checkoutService.b2bData.fingerSize ?? undefined, this.checkoutService.b2bData.isQuarterSize);
        } else {
          await this.attributeSelectionService.setConfiguredItemFromStyle(skuToUse);
        }
      }

      if (!this.selectedAttributeService.pricingObject()) {
        this.checkoutService.originalSKU = ringBuilderSKU;
        const size = this.selectedAttributeService.configuredItem().size;
        assert(size, 'Size was null');
        this.selectedAttributeService.pricingObject.set({ style: this.checkoutService.b2bData.haloStyle ?? ringBuilderSKU, size });
      }
    } catch(e) {
      console.error(e);
      console.warn('Attempting to match style');

      try {
        this.checkoutService.updateB2BData((data) => {
          data.selectedHaloStyleNumber = undefined;
          data.haloStyle = undefined;
        });

        assertTrue(!!ringBuilderSKU && !!skuToUse, 'Could not resolve provided sku or style to customizer sku');
        const styleFromSKUToUse = this.configuredItemService.isCustomSKU(skuToUse) ? this.configuredItemService.getStyleFromCustomSKU(skuToUse) : skuToUse;
        await this.attributeSelectionService.setConfiguredItemFromStyleWithEngravingAndPO(styleFromSKUToUse, b2bEng, b2bEngType, b2bPO);

        const size = this.selectedAttributeService.configuredItem().size;
        assert(size, 'Size was null');
        const [skumapEntry] = await this.skuMapService.getReverse(styleFromSKUToUse, size.value);
        if (skumapEntry) {
          this.checkoutService.updateB2BData((b2bData) => {
            b2bData.haloStyle = skumapEntry.halosku;
            b2bData.styleNumber = skumapEntry.halosku;
          });
        } else {
          this.checkoutService.updateB2BData((b2bData) => {
            b2bData.styleNumber = styleFromSKUToUse;
          });
        }

        this.checkoutService.originalSKU = ringBuilderSKU;
        this.selectedAttributeService.pricingObject.set({ style: this.checkoutService.b2bData.haloStyle ?? ringBuilderSKU, size });
      } catch(e) {
        if (!this.checkoutService.isRetailerOnly()) {
          console.error('Coult not find style or sku');
          this.dialog.open(InvalidSkuDialogComponent);
        }
        console.warn('Falling back to default style');

        const defaultSKU = this.selectedAttributeService.defaultCustomSKU;
        await this.attributeSelectionService.setConfiguredItemFromSKUWithEngravingAndPO(defaultSKU, b2bEng, b2bEngType, b2bPO);

        const defaultCustomStyle = this.configuredItemService.getStyleFromCustomSKU(defaultSKU);

        const size = this.selectedAttributeService.configuredItem().size;
        assert(size, 'Size was null');
        const [skumapEntry] = await this.skuMapService.getReverse(defaultCustomStyle, size.value);
        if (skumapEntry) {
          this.checkoutService.updateB2BData((b2bData) => {
            b2bData.haloStyle = skumapEntry.halosku;
            b2bData.selectedHaloStyleNumber = skumapEntry.halosku;
          });
        } else {
          this.checkoutService.updateB2BData((b2bData) => {
            b2bData.styleNumber = defaultCustomStyle;
          });
        }

        this.checkoutService.originalSKU = this.selectedAttributeService.defaultCustomSKU;
        this.selectedAttributeService.pricingObject.set({ style: skumapEntry?.halosku ?? defaultCustomStyle, size });
        this.loadingService.done('selected-attribute:set-item-from-style');
      }
    }

    this.isLoaded.set(true);

    if (!Object.keys(this.selectedAttributeService.configuredItem()).some(k => this.selectedAttributeService.getAttribute(k as keyof ConfiguredItem))) {
      throw 'Configured item has no data'
      // this.selectedAttributeService.setConfiguredItemFromSKU('RDCM614W-010-10');
    //   const newURL = new URL(`http://${url.host}?d=010&f=RDCM&sm=14W&pm=14W&w=6mm&s=10`);
    //   this.selectedAttributeService.setConfiguredItemFromURL(newURL);
    }

    // this.navigationService.subscribeActiveViewIndexChange(() => {
    //   if (this.isCustomizerHidden()) {
    //     this.isCustomizerHidden.set(false);
    //     this.changeDetectorRef.detectChanges();
    //     this.previewer?.resizeCanvas();
    //     this.previewer?.redrawImage();
    //   }
    // });
    this.isSmallScreen.set(window.innerWidth < this.smallScreenWidth);

    const ivl = setInterval(() => {
      if ((this.customizer?.nativeElement?.clientWidth ?? 0) > 0) {
        clearInterval(ivl);
        this.customizerWidth.set(this.customizer?.nativeElement?.clientWidth ?? 0);
        this.isCustomizerHidden.set(false);
      }
    }, 100);
  }

  async getCustomItemNumber(itemNumberOrSKU: string) {
    try {
      if (this.configuredItemService.isCustomStyle(itemNumberOrSKU) || this.configuredItemService.isCustomSKU(itemNumberOrSKU)) {
        return itemNumberOrSKU;
      }
      const mapEntry = await this.skuMapService.get(this.checkoutService.b2bData.styleNumber);
      return mapEntry?.ringBuilderSKU ?? null;
    } catch(e) {
      console.warn('Coult not map sku or style.');
      return null;
    }
  }

  ngAfterViewInit() {
    this.customizerWidth.set(this.customizer?.nativeElement?.clientWidth ?? 0);
    this.isCustomizerHidden.set(false);
  }

  customizationCategoryChange(index: number) {
    this.selectedCustomizationCategoryIndex = index;
  }

  previewerChange(previewerType: PreviewerType) {
    this.selectedPreviewerType = previewerType;
  }

  selectedAttributesChange(selectedAttributes: Map<string, string | null>) {
    this.selectedAttributes = selectedAttributes;
  }

  // resetItem() {
  //   this.selectedAttributeService.reset();
  // }

  toggleHideCustomizer() {
    this.isCustomizerHidden.set(!this.isCustomizerHidden());
    this.changeDetectorRef.detectChanges();
  }

  openChat() {
    (window as any).purechatApi.set('chatbox.expanded', true);
  }

  @HostListener('window:resize', [])
  windowResize() {
    this.isSmallScreen.set(window.innerWidth < this.smallScreenWidth);
  }
}
